// kickstart
import './utils/kickstart';

// packages
import { json } from 'd3-fetch';
import debounce from 'lodash.debounce';
import { max, rollups, sum, zip } from 'd3-array';

// navs
import './components/navbar';
import './components/linklist';

// Translation
import { __ } from './utils/i18n';

// Colors
import {
  CASES_LIGHT_BLUE,
  CASES_DARK_BLUE,
  DEATHS_LIGHT_GREY,
  DEATHS_DARK_GREY,
  VACCINES_GREEN_LIGHT,
  VACCINES_GREEN,
} from './colors';

// generic charts
import BarTimeseries from './components/BarTimeseries.svelte';
import AreaTimeseries from './components/AreaTimeseries.svelte';
import LineTimeseries from './components/LineTimeseries.svelte';
import StackedBar from './components/StackedBar.svelte';

// vaccine
import VaccineTimeseriesChart from './components/vaccines/VaccineTimeseries.svelte';

// map
import { boot as bootMapboxHotspots } from './components/places/countyDetail';
import CountyPlaceTable from './components/places/CountyPlaceTable.svelte';
import CountyPlaceTableChart from './components/places/CountyPlaceTableChart.svelte';

// unemployment
import { boot as bootUnemployment } from './components/unemployment/countyTimeseries';

// Changelog
import Changelog from './components/Changelog.svelte';

// utils
import { dateParser, pctFormat } from './utils/d3-utils';
import { loadJsonScript } from './utils/load-json-script';
import {
  getGeoJsonFromTopoJson,
  prepareTopojson,
} from './utils/prepare-topojson';
import { movingAverage } from './utils/moving-average';

//async function main() {
const timeSeriesData = loadJsonScript('timeseries')
  .filter((d) => {
    // cheat and fix this in the initial loop
    d.date = dateParser(d.date);
    // Only keep it for the chart if all agencies in the area have updated
    return (
      !d.in_progress ||
      (d.in_progress && d.agencies_updated == d.agencies_count)
    );
  })
  .sort((a, b) => a.date - b.date);

const placesData = loadJsonScript('places');
const unemploymentData = loadJsonScript('unemployment-rates');

// boot charts
if (window.INCLUDE_CUMULATIVE_CASES) {
  let casesCumAttrs = [
    {
      headline: __("Cumulative <span class='cases-blue'>cases</span>"),
      label: __('Cases'),
      value: 'confirmed_and_probable_cases',
      color: '#3995b7',
    },
  ];
  if (window.INCLUDE_NEW_DEATHS) {
    casesCumAttrs.push({
      headline: __("Cumulative <span class='deaths-gray'>deaths</span>"),
      label: __('Deaths'),
      value: 'confirmed_deaths',
      color: '#829db0',
    });
  }
  const casesCumTarget = document.getElementById('cases-timeseries-chart');
  new LineTimeseries({
    target: casesCumTarget,
    props: {
      title: __('Cumulative cases'),
      data: timeSeriesData,
      width: casesCumTarget.offsetWidth,
      margin: { top: 20, right: 80, bottom: 25, left: 70 },
      attrs: casesCumAttrs,
      sourceline: __('California Department of Public Health'),
    },
  });
}

if (window.INCLUDE_NEW_CASES) {
  const newCasesTarget = document.getElementById('new-cases-chart');
  const newCasesData = timeSeriesData.filter((d) => !d.in_progress);
  newCasesData.forEach((d) => {
    if (d.new_confirmed_and_probable_cases < 0) {
      d.new_confirmed_and_probable_cases = 0;
    }
  });

  new BarTimeseries({
    target: newCasesTarget,
    props: {
      title: __('New cases'),
      data: newCasesData,
      width: newCasesTarget.offsetWidth,
      margin: { top: 20, right: 10, bottom: 25, left: 52 },
      name: 'cases',
      attrs: [
        {
          headline: __('New <span class="cases-blue">cases</span> by day'),
          label: __('Cases'),
          value: 'new_confirmed_and_probable_cases',
          barColor: CASES_LIGHT_BLUE,
          lineColor: CASES_DARK_BLUE,
        },
      ],
      sourceline: null,
    },
  });
}

if (window.INCLUDE_NEW_DEATHS != false) {
  const newDeathsTarget = document.getElementById('new-deaths-chart');
  const newDeathsData = timeSeriesData.filter((d) => !d.in_progress);
  newDeathsData.forEach((d) => {
    if (d.new_confirmed_deaths < 0) {
      d.new_confirmed_deaths = 0;
    }
  });
  new BarTimeseries({
    target: newDeathsTarget,
    props: {
      title: __('New deaths'),
      data: newDeathsData,
      width: newDeathsTarget.offsetWidth,
      margin: { top: 20, right: 10, bottom: 25, left: 45 },
      showLine: window.INCLUDE_AVERAGES,
      name: 'deaths',
      attrs: [
        {
          headline: __('<span class="deaths-gray">Deaths</span> by day'),
          label: __('Deaths'),
          value: 'new_confirmed_deaths',
          barColor: DEATHS_LIGHT_GREY,
          lineColor: DEATHS_DARK_GREY,
        },
      ],
      sourceline: null,
    },
  });
}

// open data portal
const vaccineProgressData = loadJsonScript('vaccine-progress-timeseries')
  .map((d) => {
    d.date = dateParser(d.date);
    return d;
  })
  .filter((d) => d.new_doses_administered !== null);

const vaxAreaChart = document.getElementById('vaccine-area-chart');

new AreaTimeseries({
  target: vaxAreaChart,
  props: {
    data: vaccineProgressData,
    width: 100,
    margin: { top: 20, right: 0, bottom: 35, left: 35 },
    attrs: [
      {
        headline: __(
          'Percentage of county who are <span class="vaccines-light-green">partially</span> or <span class="vaccines-green">fully</span> vaccinated'
        ),
        label: __('Percent vaccinated'),
        value: 'pct_at_least_one_dose',
        maxValue: 1,
        layers: [
          {
            value: 'pct_fully_vaccinated',
            color: VACCINES_GREEN,
          },
          {
            value: 'pct_partially_vaccinated',
            color: VACCINES_GREEN_LIGHT,
          },
        ],
      },
    ],
    formatYTicks: pctFormat,
    sourceline: __(
      '<a href="https://data.ca.gov/dataset/covid-19-vaccine-progress-dashboard-data">California Department of Public Health</a>'
    ),
  },
});

const newVaxTarget = document.getElementById('vaccine-timeseries-chart');

vaccineProgressData.forEach((d, i) => {
  d.class = i < vaccineProgressData.length - 3 ? 'complete' : 'incomplete';
});

new BarTimeseries({
  target: newVaxTarget,
  props: {
    title: __('New vaccinations by day'),
    data: vaccineProgressData,
    width: newVaxTarget.offsetWidth,
    margin: { top: 20, right: 10, bottom: 25, left: 70 },
    name: 'vaccines',
    attrs: [
      {
        headline: __(
          '<span class="vaccines-light-green">Vaccine doses</span> administered by day'
        ),
        label: __('Vaccines'),
        value: 'new_doses_administered',
        barColor: VACCINES_GREEN_LIGHT,
        lineColor: VACCINES_GREEN,
        lineFunc: (data) => {
          return zip(
            data.map((d) => d.date),
            movingAverage(
              data.map((d) => d.value),
              7
            ).slice(0, -3)
          );
        },
      },
    ],
    footnote: __(
      'Due to delays in data collection, totals for the most recent days are typically incomplete.'
    ),
    sourceline: __('California Department of Public Health'),
  },
});

// age bar chart
const ageLatest = loadJsonScript('age-data')
  .map((d) => {
    d.unvaccinated_percent = ((1 - d.at_least_one_dose_percent) * 100).toFixed(
      0
    );
    d.at_least_one_dose_percent = (d.at_least_one_dose_percent * 100).toFixed(
      0
    );
    d.fully_vaccinated_percent = (d.fully_vaccinated_percent * 100).toFixed(0);
    d.partially_vaccinated_percent = (
      d.partially_vaccinated_percent * 100
    ).toFixed(0);
    return d;
  })
  .filter((d) => d.at_least_one_dose_percent !== null);

const ageTarget = document.getElementById('age-latest-chart');

let ageFiltered = ageLatest.filter((d) => d.population_total > 100);

ageFiltered.forEach((element) => {
  let title = element.age_group;

  new StackedBar({
    target: ageTarget,
    props: {
      width: ageTarget.offsetWidth,
      height: 70,
      labelHeight: 30,
      title: title,
      titleClass: 'normal',
      data: element,
      attrs: [
        {
          key: 'fully_vaccinated_percent',
          name: 'Fully vaccinated',
          color: VACCINES_GREEN,
        },
        {
          key: 'partially_vaccinated_percent',
          name: 'Partially vaccinated',
          color: VACCINES_GREEN_LIGHT,
        },
        {
          key: 'unvaccinated_percent',
          name: 'Unvaccinated',
          color: '#efefef',
        },
      ],
      source: __(
        '<a href="https://data.ca.gov/dataset/covid-19-vaccine-progress-dashboard-data">California Department of Public Health</a>'
      ),
    },
  });
});

// race bar chart
if (window.INCLUDE_VACCINES_BY_RACE) {
  let raceLatest = loadJsonScript('race-data');

  if (raceLatest.length > 2) {
    raceLatest.forEach((d) => {
      d.unvaccinated_percent = (
        (1 - d.at_least_one_dose_percent) *
        100
      ).toFixed(0);
      d.at_least_one_dose_percent = (d.at_least_one_dose_percent * 100).toFixed(
        0
      );
      d.fully_vaccinated_percent = (d.fully_vaccinated_percent * 100).toFixed(
        0
      );
      d.partially_vaccinated_percent = (
        d.partially_vaccinated_percent * 100
      ).toFixed(0);
    });

    const raceTarget = document.getElementById('race-latest-chart');

    let raceSort = raceLatest.sort(
      (a, b) => a.at_least_one_dose_percent - b.at_least_one_dose_percent
    );

    raceSort
      .filter((d) => d.race_label != 'Unknown')
      .filter((d) => d.population_total > 100)
      .forEach((element) => {
        let title = element.race_label;

        new StackedBar({
          target: raceTarget,
          props: {
            width: raceTarget.offsetWidth,
            height: 70,
            labelHeight: 30,
            title: title,
            titleClass: 'normal',
            data: element,
            attrs: [
              {
                key: 'fully_vaccinated_percent',
                name: 'Fully vaccinated',
                color: VACCINES_GREEN,
              },
              {
                key: 'partially_vaccinated_percent',
                name: 'Partially vaccinated',
                color: VACCINES_GREEN_LIGHT,
              },
              {
                key: 'unvaccinated_percent',
                name: 'Unvaccinated',
                color: '#efefef',
              },
            ],
            source: __(
              '<a href="https://data.ca.gov/dataset/covid-19-vaccine-progress-dashboard-data">California Department of Public Health</a>'
            ),
          },
        });
      });
  }
}

if (window.INCLUDE_MAP) {
  const mapData = loadJsonScript('topojson');
  const places = getGeoJsonFromTopoJson(mapData, 'places');
  bootMapboxHotspots(
    placesData.places,
    places.collection,
    places.bbox,
    window.CITIES_TIMESERIES
  );
  const cityTableKlass = window.CITIES_TIMESERIES
    ? CountyPlaceTableChart
    : CountyPlaceTable;
  new cityTableKlass({
    target: document.getElementById('county-places-list'),
    props: {
      data: placesData,
      include_percapita: window.CITIES_PERCAPITA,
    },
  });
}

if (window.INCLUDE_HOSPITAL_PATIENTS) {
  const patientsTarget = document.getElementById('patients-chart');
  const patientsData = loadJsonScript('patients-timeseries').map((d) => {
    // cheat and fix this in the initial loop
    d.date = dateParser(d.date);
    return d;
  });
  const patientsMaxValue = max(
    patientsData.map((d) => {
      return sum([d.total_icu_patients, d.total_acute_patients]);
    })
  );
  new AreaTimeseries({
    target: patientsTarget,
    props: {
      title: __('Cumulative cases'),
      data: patientsData,
      width: patientsTarget.offsetWidth,
      attrs: [
        {
          headline: __(
            '<span class="cases-blue">Intensive care</span> and <span class="cases-light-blue">other</span> hospitalized patients'
          ),
          label: __('Positive'),
          value: 'positive',
          maxValue: patientsMaxValue,
          layers: [
            {
              value: 'icu_positive_patients',
              color: '#3995b7',
            },
            {
              value: 'acute_positive_patients',
              color: '#c0e9f3',
            },
          ],
        },
        {
          headline: __(
            '<span class="cases-blue">Intensive care</span> and <span class="cases-light-blue">other</span> hospitalized patients'
          ),
          label: __('Suspected'),
          value: 'suspected',
          maxValue: patientsMaxValue,
          layers: [
            {
              value: 'icu_suspected_patients',
              color: '#3995b7',
            },
            {
              value: 'acute_suspected_patients',
              color: '#c0e9f3',
            },
          ],
        },
        {
          headline: __(
            '<span class="cases-blue">Intensive care</span> and <span class="cases-light-blue">other</span> hospitalized patients'
          ),
          label: __('Both'),
          value: 'both',
          maxValue: patientsMaxValue,
          layers: [
            {
              value: 'total_icu_patients',
              color: '#3995b7',
            },
            {
              value: 'total_acute_patients',
              color: '#c0e9f3',
            },
          ],
        },
      ],
      sourceline: __(
        '<a href="https://data.ca.gov/dataset/covid-19-hospital-data">California Department of Public Health</a>'
      ),
    },
  });
}

// Testing charts
//const newTestsTarget = document.getElementById('testing-timeseries-chart');
// const testingTimeseries = loadJsonScript('testing-timeseries').map((d) => {
//   d.date = dateParser(d.date);
//   return d;
// });

// const newTestsData = testingTimeseries.filter(
//   (d) => d.date > dateParser('2020-04-29') && d.new_tests
// );
// newTestsData.forEach((d) => {
//   if (d.new_tests < 0) {
//     d.new_tests = 0;
//   }
// });

// newTestsData.forEach((d, i) => {
//   d.class = i < newTestsData.length - 7 ? 'complete' : 'incomplete';
// });

// const postivityRate = testingTimeseries.filter(
//   (d) => d.date > dateParser('2020-04-29')
// );

// postivityRate.forEach((d) => {
//   if (d.positive_test_rate_seven_day_total < 0) {
//     d.positive_test_rate_seven_day_total = 0;
//   }
// });

// new BarTimeseries({
//   target: newTestsTarget,
//   props: {
//     title: __('New tests by day'),
//     data: newTestsData,
//     width: newTestsTarget.offsetWidth,
//     margin: { top: 20, right: 10, bottom: 25, left: 70 },
//     name: 'tests',
//     attrs: [
//       {
//         headline: __("New <span class='cases-blue'>tests</span> by day"),
//         label: __('Tests'),
//         value: 'new_tests',
//         barColor: CASES_LIGHT_BLUE,
//         lineColor: CASES_DARK_BLUE,
//         lineFunc: (data) => {
//           return zip(
//             data.map((d) => d.date),
//             movingAverage(
//               data.map((d) => d.value),
//               7
//             ).slice(0, -7)
//           );
//         },
//       },
//     ],
//     footnote: __(
//       'Due to delays in data collection, totals for the most recent days are incomplete.'
//     ),
//     sourceline: __('California Department of Public Health'),
//   },
// });

// const testRateTarget = document.getElementById('testing-positive-rate-chart');
// new LineTimeseries({
//   target: testRateTarget,
//   props: {
//     title: __('Positive test rate, seven-day average'),
//     data: postivityRate.slice(0, -1),
//     width: testRateTarget.offsetWidth,
//     margin: { top: 20, right: 65, bottom: 25, left: 35 },
//     name: postivityRate[0].county,
//     attrs: [
//       {
//         headline: __(
//           __(
//             "Positive <span class='cases-blue'>test</span> rate, seven-day average"
//           )
//         ),
//         label: 'Positive test rate',
//         value: 'positive_test_rate_seven_day_total',
//         color: '#3995b7',
//       },
//     ],
//     footnote: __(
//       'Due to delays in data collection, totals for the most recent days are incomplete.'
//     ),
//     sourceline: __('California Department of Public Health'),
//     formatLastValue: (d) => `${(d.value * 100).toFixed(1)}%`,
//     formatYTicks: pctFormat,
//   },
// });

// Changelog
new Changelog({
  target: document.getElementById('changelog-container'),
  props: loadJsonScript('changelog-data'),
});
